import React, { ReactElement, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import { xIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import moment from 'moment';
import styles from './email-actions-dialog.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { MultiSelectEmailController, Person } from '@components-clean/core/multi-select-email/multi-select-email';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextBox } from '@components-clean/core/text-box';
import { ActionsEmailPreviewDto } from '@interfaces/central-monitoring';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowMessage, ShowError } from '@utils/notification';
import {
    getStudyMembers,
    getStudyMembers_OpraQueries_studyMembers_StudyMember,
    getStudyMembersVariables,
} from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';
import ActionsApi from '@apis/bff-api/central-monitoring/actions';

export interface Props {
    onClose: () => void;
    previewData: ActionsEmailPreviewDto | null;
    studyRef: string;
}

interface PreviewProps {
    data: ActionsEmailPreviewDto;
    studyRef: string;
    onClose: () => void;
}

const LabelText = ({ children, inline }: React.PropsWithChildren & { inline?: boolean }): ReactElement => {
    return <span className={cn(' k-font-weight-medium', { 'k-pr-2': inline })}>{children}</span>;
};

const EmailControlsActionsPreview = ({ data, studyRef, onClose }: PreviewProps) => {
    const { state } = useLocalState();
    const { trigger, isMutating: sendingEmail } = ActionsApi.useSendEmail(studyRef);
    const { t }: { t: (key: string, options?: Record<string, string | null | undefined>) => string } = useTranslation([
        'cm',
        'common',
    ]);

    const [studyMembers, setStudyMembers] = useState<Person[]>([]);

    const { data: studyMembersData, loading: loadingStudyMembers } = useQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS, {
        variables: { studyId: studyRef },
    });

    useEffect(() => {
        if (studyMembersData?.studyMembers) {
            const studyUsers = studyMembersData.studyMembers
                ?.filter(
                    (user): user is getStudyMembers_OpraQueries_studyMembers_StudyMember =>
                        !!user &&
                        user.active === true &&
                        !!user.email &&
                        !!user.firstName &&
                        !!user.lastName &&
                        user.email !== data.ownerEmail,
                )
                .map((user) => ({
                    firstName: user.firstName!,
                    lastName: user.lastName!,
                    email: user.email!,
                }));
            setStudyMembers(studyUsers);
        }
    }, [studyMembersData?.studyMembers]);

    const {
        control,
        formState: { isValid },
        getValues,
        handleSubmit,
    } = useForm<{ cc: string; notes: string }>({
        mode: 'all',
        defaultValues: {
            cc: '',
            notes: '',
        },
    });

    const onSubmit = async () => {
        const requestValues = {
            to: data.ownerEmail,
            ccEmails: getValues('cc'),
            actions: data.actions?.flatMap((a) => (a.id && [a.id]) || []),
            timestamp: data.timestamp,
            notes: getValues('notes'),
        };
        try {
            const response = await trigger(requestValues);

            if (response) ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
            else ShowError(t('common:errorMessage'));
        } catch {
            ShowError(t('common:errorMessage'));
        } finally {
            onClose();
        }
    };

    const body = (
        <div className="k-h-full k-w-full">
            <div className="k-w-full k-d-flex-col k-justify-content-start k-align-items-start">
                <Card
                    data-testid="actions-email-preview-details"
                    title={t('cm:cmActionAlert', { studyNumber: state?.selectedStudy?.studyNumber })}
                    className="k-w-full"
                >
                    <div className="k-w-full" style={{ height: 327 }}>
                        <form id="email-actions">
                            <div className="k-mb-2">
                                <TextBox
                                    label={t('cm:monitoringToolkit.emailActions.to')}
                                    data-testid="email-actions-owner-email"
                                    readOnly
                                    value={data.ownerEmail || undefined}
                                />
                            </div>
                            <div className="k-mb-2">
                                <MultiSelectEmailController
                                    data-testid="email-actions-cc-emails"
                                    label={t('cm:monitoringToolkit.emailActions.cc')}
                                    control={control}
                                    name="cc"
                                    data={studyMembers}
                                    loading={loadingStudyMembers}
                                    placeholder={t('cm:monitoringToolkit.emailActions.ccHint')}
                                />
                            </div>
                            <div>
                                <TextAreaController
                                    className={styles.notesTextArea}
                                    data-testid="email-actions-notes"
                                    maxLength={4000}
                                    label={t('cm:monitoringToolkit.emailActions.notes')}
                                    control={control}
                                    name="notes"
                                    placeholder={t('cm:monitoringToolkit.emailActions.notesHint')}
                                    validationTooltipPosition="auto"
                                />
                            </div>
                        </form>
                    </div>
                </Card>
                {data.controlObservations
                    ?.sort((a, b) => moment(b.createdDate).diff(moment(a.createdDate)))
                    ?.map((observation, i, row) => {
                        const obsActions = data.actions?.filter((a) => a.observationRecordId === observation.recordId);
                        const isLastItem = i + 1 === row.length;
                        const isFirstItem = i === 0;
                        return (
                            <>
                                <Card
                                    data-testid={`email-actions-observation-${observation.recordId}`}
                                    title={`Observation ID - ${observation.recordId}`}
                                    className={cn(isFirstItem ? 'k-mt-6' : 'k-mt-3.5', styles.cardCustomBorder)}
                                    bodyStyles={{ padding: 0 }}
                                >
                                    <div className="k-d-flex-col">
                                        <div
                                            className={cn(
                                                styles.infoBlock,
                                                styles.controlsInfo,
                                                'k-d-flex-row k-justify-content-between k-align-items-center k-font-size-lg k-py-3 k-px-7.5',
                                            )}
                                        >
                                            <span>
                                                <LabelText inline>{t('cm:observationDialog.panel')}</LabelText>
                                                {observation.panel || t('cm:n/a')}
                                            </span>
                                            <span>
                                                <LabelText inline>
                                                    {t('cm:observationDialog.timePeriodOptional')}
                                                </LabelText>
                                                {stringDateWithoutTimeFromUtc(observation.timePeriod, 'DD-MMM-YYYY') ||
                                                    t('cm:n/a')}
                                            </span>
                                            <span>
                                                <LabelText inline>
                                                    {t('cm:observationDialog.datasetOpenedOnDate')}
                                                </LabelText>
                                                {stringDateWithoutTimeFromUtc(
                                                    observation.datasetOpenedOn,
                                                    'DD-MMM-YYYY',
                                                ) || t('cm:n/a')}
                                            </span>
                                            <span>
                                                <LabelText inline>
                                                    {t('cm:observationDialog.observationDateLabel')}
                                                </LabelText>
                                                {stringDateWithoutTimeFromUtc(
                                                    observation.observationDate,
                                                    'DD-MMM-YYYY',
                                                ) || t('cm:n/a')}
                                            </span>
                                            <span>
                                                <LabelText inline>{t('cm:observationDialog.closedDate')}</LabelText>
                                                {stringDateWithoutTimeFromUtc(observation.closedDate, 'DD-MMM-YYYY') ||
                                                    t('cm:n/a')}
                                            </span>
                                        </div>
                                        <div className="k-py-3 k-px-7.5 k-font-size-md">
                                            <div>
                                                <div>
                                                    <p className="k-font-weight-medium k-font-size-lg">
                                                        {t('cm:observationDialog.observationLabel')}
                                                    </p>
                                                    <p>{observation.observation || t('cm:n/a')}</p>
                                                </div>
                                                <div>
                                                    <p className="k-font-weight-medium k-font-size-lg">
                                                        {t('cm:observationDialog.rootCause')}
                                                    </p>
                                                    <p>{observation.rootCause || t('cm:n/a')}</p>
                                                </div>
                                                <div>
                                                    <p className="k-font-weight-medium k-font-size-lg">
                                                        {t('cm:observationDialog.comments')}
                                                    </p>
                                                    <p>{observation.comments || t('cm:n/a')}</p>
                                                </div>
                                                <div className="k-font-size-lg">
                                                    <span className="k-font-weight-medium k-pr-2">
                                                        {t('cm:observationDialog.created')}
                                                    </span>
                                                    <span>
                                                        {stringDateWithoutTimeFromUtc(
                                                            observation.createdDate,
                                                            'DD-MMM-YYYY',
                                                        ) || t('cm:n/a')}
                                                    </span>
                                                    <span>{t('cm:observationDialog.by')}</span>
                                                    <span>{observation.createdBy}</span>
                                                </div>
                                            </div>
                                            {obsActions
                                                ?.sort((a, b) => moment(b.createdDate).diff(moment(a.createdDate)))
                                                ?.map((action) => {
                                                    return (
                                                        <Card
                                                            data-testid={`email-actions-action-${action.recordId}`}
                                                            className="k-mt-6"
                                                            title={`Action ID - ${action.recordId}`}
                                                            padding="none"
                                                        >
                                                            <div className="k-d-flex-col k-font-size-lg">
                                                                <div
                                                                    className={cn(
                                                                        styles.infoBlock,
                                                                        'k-d-flex-row k-justify-content-start k-py-3 k-px-7.5',
                                                                    )}
                                                                >
                                                                    <span style={{ minWidth: 276 }}>
                                                                        <LabelText inline>
                                                                            {t('actionDialog.requestedDateLabel')}
                                                                        </LabelText>
                                                                        {stringDateWithoutTimeFromUtc(
                                                                            action.requestedDate,
                                                                            'DD-MMM-YYYY',
                                                                        ) || t('cm:n/a')}
                                                                    </span>
                                                                    <span>
                                                                        <LabelText inline>
                                                                            {t('actionDialog.owner')}
                                                                        </LabelText>
                                                                        {action.owner || t('cm:n/a')}
                                                                    </span>
                                                                </div>
                                                                <div className="k-py-3 k-px-7.5">
                                                                    <div>
                                                                        <span className="k-font-weight-medium">
                                                                            {t('actionDialog.descriptionLabel')}
                                                                        </span>
                                                                        <p>{action.description || t('cm:n/a')}</p>
                                                                    </div>
                                                                    <div>
                                                                        <span className="k-font-weight-medium">
                                                                            {t('actionDialog.comments')}
                                                                        </span>
                                                                        <p>{action.comments || t('cm:n/a')}</p>
                                                                    </div>
                                                                    <div>
                                                                        <span className="k-font-weight-medium">
                                                                            {t('actionDialog.resolution')}
                                                                        </span>
                                                                        <p>{action.resolution || t('cm:n/a')}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Card>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                </Card>
                                {!isLastItem && <hr className={styles.hrCustom} />}
                            </>
                        );
                    })}
            </div>
        </div>
    );
    return (
        <DraggableDialog
            dataTestId="email-actions-warning-dialog"
            draggable={false}
            title={t('monitoringToolkit.emailActions.previewTitle')}
            onSubmit={undefined}
            primaryBtnLabel={undefined}
            stage="FULLSCREEN"
            width={473}
            hideTitleCloseButton
            titleBarStyle="alternate"
            restoreButton={() => {
                return (
                    <div className="k-d-flex k-flex-row k-gap-1">
                        <Button
                            data-testid="preview-email-actions-cancel"
                            onClick={onClose}
                            primary
                            disabled={sendingEmail}
                        >
                            {t('common:cancel')}
                        </Button>
                        <Button
                            fillMode="solid"
                            data-testid="preview-email-actions-send"
                            onClick={() => handleSubmit(onSubmit)()}
                            disabled={!isValid || sendingEmail}
                            type="submit"
                        >
                            {t('common:sendEmail')}
                        </Button>
                    </div>
                );
            }}
            setHeight="fit-content"
        >
            {body}
        </DraggableDialog>
    );
};

const EmailActionsDialog = ({ onClose, previewData, studyRef }: Props) => {
    const { t } = useTranslation('cm');

    return !previewData ? (
        <DraggableDialog
            dataTestId="email-actions-warning-dialog"
            draggable={false}
            title={t('monitoringToolkit.emailActions.warningTitle')}
            onSubmit={onClose}
            primaryBtnLabel={t('common:close')}
            height={145}
            stage="DEFAULT"
            width={478}
            minimizeButton={() => null}
            style={{}}
            closeButton={() => (
                <Button
                    data-testid="close-title-button"
                    onClick={onClose}
                    type="button"
                    fillMode="flat"
                    svgIcon={xIcon}
                />
            )}
        >
            <div className="k-d-flex-col">
                <InfoField message={t('monitoringToolkit.emailActions.warningBody')} type="Warning" />
            </div>
        </DraggableDialog>
    ) : (
        <EmailControlsActionsPreview data={previewData} studyRef={studyRef} onClose={onClose} />
    );
};

export default EmailActionsDialog;
